import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Video, Calendar, AlertTriangle, Globe } from "lucide-react";
import { useMutation, useQuery } from "@apollo/client/react";
import { START_SESSION } from "@/graphql/mutations";
import { GET_CURRENT_USER } from "@/graphql/queries";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import { getAccessToken } from "@/lib/token-utils";
import { trackDashboardEvent } from "@/lib/analytics";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";


interface StartSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credits: number;
  isPersonalPlan: boolean;
  overageLockActive: boolean;
}

const StartSessionDialog = ({
  open,
  onOpenChange,
  credits,
  isPersonalPlan,
  overageLockActive,
}: StartSessionDialogProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();

  const hasInsufficientCredits =
    isPersonalPlan && credits !== undefined && credits <= 0;
  const hasOverageLock = !isPersonalPlan && overageLockActive;

  // Get organization ID from user's organizations
  const { data: userData, loading: userLoading } = useQuery<{ me?: { organizations?: { id: string; name: string; role: string; isPersonal?: boolean }[] } | null }>(GET_CURRENT_USER);
  const organizations = userData?.me?.organizations || [];
  // Use shared hook to get current organization consistently
  const { currentOrganizationId } = useCurrentOrganization(organizations);
  const organizationId = currentOrganizationId || null;

  const [startSession, { loading }] = useMutation(START_SESSION, {
    onCompleted: (data: { startSession?: { flowRoomName?: string } }) => {
      trackDashboardEvent("session_action", {
        action: "start_success",
        session_title: title.trim(),
        destination: data.startSession?.flowRoomName ? "livestream" : "history",
      });
      toast({
        title: "Session started",
        description: "Your streaming session has been started successfully.",
      });
      onOpenChange(false);

      // Open livestream in a new tab (moderator status determined by auth token on backend)
      const session = data.startSession;
      if (session?.flowRoomName) {
        // Get auth token to pass to livestream app (needed because different origin)
        const authToken = getAccessToken();

        // Get livestream URL based on environment
        const getLivestreamUrl = () => {
          const baseUrl = typeof window !== 'undefined' &&
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
            ? `http://localhost:3001/${session.flowRoomName}`
            : `https://meet.flowlabs.live/${session.flowRoomName}`;

          // Add auth token as URL parameter if available
          if (authToken) {
            return `${baseUrl}?token=${encodeURIComponent(authToken)}`;
          }
          return baseUrl;
        };

        // Open in a new tab
        window.open(getLivestreamUrl(), '_blank');
      } else {
        // Fallback to history if no room name
        navigate('/history');
      }
    },
    onError: (error) => {
      trackDashboardEvent("session_action", {
        action: "start_failure",
        session_title: title.trim(),
        error_message: error.message || "start_session_failed",
      });
      toast({
        title: "Error",
        description: error.message || "Failed to start session. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleStartSession = async () => {
    if (!organizationId) {
      toast({
        title: "Error",
        description: "No organization found. Please complete onboarding first.",
        variant: "destructive",
      });
      return;
    }

    if (hasInsufficientCredits) {
      toast({
        title: "Insufficient Credits",
        description: "You need a positive prepaid credit balance to start a session.",
        variant: "destructive",
      });
      return;
    }

    if (hasOverageLock) {
      toast({
        title: "Overage Lock Active",
        description: "Settle unpaid billable overage to start a new session.",
        variant: "destructive",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        title: "Error",
        description: "Session title is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      trackDashboardEvent("session_action", {
        action: "start_attempt",
        session_title: title.trim(),
        has_description: Boolean(description),
        has_notes: Boolean(notes),
      });
      await startSession({
        variables: {
          input: {
            duration: 1, // Default 1 hour
            participants: 5, // Default 5 participants
            quality: "HIGH", // Default HIGH quality
            title: title.trim(),
            ...(description && { description }),
            ...(notes && { notes }),
          },
        },
      });
    } catch (error) {
      // Error handled by onError callback
    }
  };

  const handleClose = () => {
    setTitle("");
    setDescription("");
    setNotes("");
    onOpenChange(false);
  };

  const handleScheduleForLater = () => {
    handleClose();
    navigate('/rooms');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg bg-card border-border max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Start New Session</DialogTitle>
          <DialogDescription>
            Give your session a title to get started
          </DialogDescription>
        </DialogHeader>



        <div className="space-y-4 py-4">
          {/* Personal prepaid guardrail */}
          {hasInsufficientCredits && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Insufficient Credits</AlertTitle>
              <AlertDescription>
                You need a positive prepaid credit balance to start a session. Please{" "}
                <a href="/credits" className="underline font-medium hover:no-underline" onClick={handleClose}>
                  top up your credits
                </a>{" "}
                to continue.
              </AlertDescription>
            </Alert>
          )}

          {/* Subscription overage lock */}
          {hasOverageLock && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Overage Lock Active</AlertTitle>
              <AlertDescription>
                New sessions are locked until unpaid billable overage is settled.
              </AlertDescription>
            </Alert>
          )}

          {/* Session Metadata */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Session Title <span className="text-destructive">*</span></Label>
              <Input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="bg-background border-border"
                placeholder="e.g., Team Standup, Client Meeting"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-background border-border min-h-[80px]"
                placeholder="Brief description of the session"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-background border-border min-h-[100px]"
                placeholder="Add notes for this session (can be edited during and after session)"
              />
            </div>


          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="outline"
            onClick={handleScheduleForLater}
            disabled={loading}
            className="gap-2"
          >
            <Calendar className="w-4 h-4" />
            Schedule for Later
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStartSession}
              disabled={loading || userLoading || !organizationId || !title.trim() || hasInsufficientCredits || hasOverageLock}
              className="bg-gradient-hero text-white hover:opacity-90 font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Starting...
                </>
              ) : userLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : !organizationId ? (
                "No Organization"
              ) : hasInsufficientCredits ? (
                "Insufficient Credits"
              ) : hasOverageLock ? (
                "Overage Lock Active"
              ) : (
                "Start Session"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StartSessionDialog;
